// @ts-nocheck
import React, { useMemo, useState } from "react";
import { ReactComponent as CaretDownIcon } from "../../assets/icons/caret-down.svg";
import { ReactComponent as CaretUpIcon } from "../../assets/icons/caret-up.svg";
import { ReactComponent as PlusIconBlack } from "../../assets/icons/plus-black.svg";
import { ReactComponent as FlowRateIcon } from "../../assets/icons/flow-rate.svg";
import { ReactComponent as PrecipitationIcon } from "../../assets/icons/precipitation.svg";
import { ReactComponent as TemperatureIcon } from "../../assets/icons/temperature.svg";
import { ReactComponent as SnowDepthIcon } from "../../assets/icons/snow-depth.svg";
import { ReactComponent as FlagIcon } from "../../assets/icons/flag.svg";
import { ReactComponent as KebabIcon } from "../../assets/icons/kebab.svg";
import AssetMapView from "../../components/AssetMapView/AssetMapView";

// This component represents the Asset Map section of the Asset Dashboard. It includes model and channel filters
// on the left sidebar and the map view on the right.
type CheckboxToggleProps = { // Accessible checkbox toggle component props
  checked: boolean;
  onChange?: () => void;
  ariaLabel: string;
};
// Accessible checkbox toggle component
const CheckboxToggle = ({ checked, onChange, ariaLabel }: CheckboxToggleProps) => (
  <label className="inline-flex items-center">
    <input
      type="checkbox"
      className="peer sr-only"
      checked={checked}
      onChange={onChange}
      aria-label={ariaLabel}
    />
    <div
      className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${
        checked ? "border-[#879643] bg-[#879643] text-white" : "border-[#cbd5e1] bg-white text-transparent"
      }`}
    >
      <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3.5 8.5l2.5 2.5L12.5 5" />
      </svg>
    </div>
  </label>
);
// Model and Channel data structure
type ModelItem = {
  label: string;
  checked: boolean;
  icon: React.ReactNode;
};
// Predefined model sections with items. just frontend data
const modelSections: { title: string; items: ModelItem[] }[] = [
  {
    title: "Model Name",
    items: [
      { label: "Flow Rate", checked: true, icon: <FlowRateIcon className="h-5 w-5" /> },
      { label: "Precipitation", checked: true, icon: <PrecipitationIcon className="h-5 w-5" /> },
      { label: "Temperature", checked: false, icon: <TemperatureIcon className="h-5 w-5" /> },
      { label: "Snow Depth", checked: false, icon: <SnowDepthIcon className="h-5 w-5" /> },
    ],
  },
];

const channels = ["New Channel Name", "New Channel Name"]; // Predefined channels just frontend data
// Props for AssetMap component
interface AssetItem {
  [key: string]: unknown;
}
interface AssetGroup {
  [key: string]: unknown;
}
type AssetMapProps = {
  assetItems?: any[];
  assetGroup?: any;
};
// Main AssetMap component renders the sidebar and map view.
const AssetMap = ({ assetItems = [], assetGroup = null }: AssetMapProps) => {
  const [modelState, setModelState] = useState(() =>
    modelSections.map((section) => section.items.map((item) => item.checked))
  );
  const [channelState, setChannelState] = useState(() => channels.map(() => false));
  const [sectionOpen, setSectionOpen] = useState(() => modelSections.map(() => true));
  // Memoized models with their current checked state
  const modelsWithState = useMemo(
    () =>
      modelSections.map((section, sectionIndex) => ({
        ...section,
        items: section.items.map((item, itemIndex) => ({
          ...item,
          checked: modelState[sectionIndex]?.[itemIndex] ?? false,
        })),
      })),
    [modelState]
  );

  return (
    <div className="flex h-full gap-3">
      {/* Sidebar for Models and Channels */}
      <div className="hidden shrink-0 flex-col gap-3 rounded-[8px] border border-eai-bg-lt2 bg-white p-4 lg:flex">
        <div className="text-xs uppercase tracking-[0.16em] text-slate-400">Models</div>
        {/* Model sections */}
        {modelsWithState.map((section, index) => (
          <div key={`${section.title}-${index}`} className="flex flex-col gap-3">
            {/* Section header with toggle and kebab menu */}
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-eai-dark">{section.title}</h4>
              <div className="inline-flex items-center gap-3 text-slate-400">
                <button
                  type="button"
                  className="inline-flex items-center -translate-x-[7px]"
                  aria-expanded={sectionOpen[index]}
                  aria-controls={`model-section-${index}`}
                  onClick={() =>
                    setSectionOpen((prev) => prev.map((open, i) => (i === index ? !open : open)))
                  }
                >
                  {sectionOpen[index] ? (
                    <CaretUpIcon className="h-1 w-2" />
                  ) : (
                    <CaretDownIcon className="h-1 w-2" />
                  )}
                </button>
                <KebabIcon className="h-5 w-5" />
              </div>
            </div>
            {/* Model items */}
            {sectionOpen[index] && (
              <div id={`model-section-${index}`} className="flex flex-col gap-3">
                {section.items.map((item, itemIndex) => (
                  <div key={item.label} className="grid grid-cols-[20px_1fr_20px_20px] items-center gap-3">
                    <div className="text-slate-600">{item.icon}</div>
                    <div className="text-sm text-slate-700">{item.label}</div>
                    <CheckboxToggle
                      checked={item.checked}
                      ariaLabel={`${item.label} toggle`}
                      onChange={() =>
                        setModelState((prev) =>
                          prev.map((sectionState, sIndex) =>
                            sIndex !== index
                              ? sectionState
                              : sectionState.map((checked, iIndex) =>
                                  iIndex === itemIndex ? !checked : checked
                                )
                          )
                        )
                      }
                    />
                    <div />
                  </div>
                ))}
              </div>
            )}
            {index < modelSections.length - 1 && <div className="border-t border-dashed border-eai-bg-lt2" />} {/* Divider between sections */}
          </div>
        ))}
        {/* Channels section */}
        <div className="border-t border-dashed border-eai-bg-lt2 pt-3">
          <div className="text-xs uppercase tracking-[0.16em] text-slate-400">Channels</div>
          <div className="mt-3 flex flex-col gap-3">
            {channels.map((channel, channelIndex) => (
              <div
                key={channel}
                className="grid grid-cols-[20px_1fr_20px_20px] items-center gap-3 text-sm text-slate-700"
              >
                <FlagIcon className="h-5 w-5" />
                <span>{channel}</span>
                <CheckboxToggle
                  checked={channelState[channelIndex]}
                  ariaLabel={`${channel} toggle`}
                  onChange={() =>
                    setChannelState((prev) =>
                      prev.map((checked, iIndex) => (iIndex === channelIndex ? !checked : checked))
                    )
                  }
                />
                <KebabIcon className="h-5 w-5 justify-self-end text-slate-400" />
              </div>
            ))}
          </div>
        </div>
        <button
          type="button"
          className="mt-auto inline-flex h-10 items-center justify-center gap-2 rounded-[8px] border border-eai-bg-lt2 bg-white text-sm font-medium text-eai-dark shadow-[0_1px_2px_rgba(16,24,40,0.06)]"
        >
          New Channel
          <PlusIconBlack className="h-4 w-4" />
        </button>
      </div>
     {/* Asset Map View Container */}
      <div className="relative flex-1 overflow-hidden rounded-[8px] border border-eai-bg-lt2 bg-white">
        <AssetMapView
          assetItems={assetItems}
          assetGroup={assetGroup}
          isEmbedded={true}
          showWeatherData={false}
          showOnlyAssetData={true}
        />
      </div>
    </div>
  );
};



export default AssetMap;
